import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, TextField, MenuItem, Button, Grid,
    CircularProgress, Divider, Chip, IconButton, Tooltip, Dialog,
    DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import GrassIcon from '@mui/icons-material/Grass';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-hot-toast';
import { api } from '../../services/api';
import { STAGE_LABELS } from '../../utils/labels';

const STAGES = ['seedling', 'vegetative', 'flowering', 'harvested'];
const DEVICE_ID = 'esp32_001';

export default function PlantDetail() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [plant, setPlant] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editOpen, setEditOpen] = useState(false);
    const [finishOpen, setFinishOpen] = useState(false);
    const [dryOpen, setDryOpen] = useState(false);
    const [finishType, setFinishType] = useState('harvested');
    const [wetWeight, setWetWeight] = useState('');
    const [dryWeight, setDryWeight] = useState('');
    const [form, setForm] = useState({});

    const load = () => {
        setLoading(true);
        api.get(`/plants/${id}`).then(res => { setPlant(res.data); setLoading(false); })
            .catch(() => { setLoading(false); toast.error('Erro ao carregar planta'); });
    };

    useEffect(load, [id]);

    const openEdit = () => {
        setForm({
            name: plant.name, strain: plant.strain || '', growthStage: plant.growthStage,
            wateringAmount: plant.wateringAmount || 100, notes: plant.notes || '',
            startDate: plant.startDate ? new Date(plant.startDate).toISOString().slice(0, 10) : ''
        });
        setEditOpen(true);
    };

    const handleEdit = async () => {
        if (!form.name.trim()) { toast.error('Informe o nome da planta'); return; }
        try {
            const payload = { ...form };
            if (form.startDate) payload.startDate = new Date(form.startDate).toISOString();
            await api.put(`/plants/${id}`, payload);
            toast.success('Planta atualizada');
            setEditOpen(false);
            load();
        } catch (e) { toast.error('Erro ao atualizar planta'); }
    };

    const openFinish = () => { setFinishType('harvested'); setWetWeight(''); setFinishOpen(true); };

    const handleFinish = async () => {
        try {
            const payload = { endType: finishType };
            if (finishType === 'harvested' && wetWeight) payload.wetWeight = Number(wetWeight);
            await api.post(`/plants/${id}/finish`, payload);
            toast.success(finishType === 'harvested' ? 'Planta colhida!' : 'Planta finalizada');
            setFinishOpen(false);
            load();
        } catch (e) { toast.error('Erro ao finalizar planta'); }
    };

    const openDry = () => { setDryWeight(''); setDryOpen(true); };
    const handleDry = async () => {
        try {
            await api.post(`/plants/${id}/dry-weight`, { dryWeight: Number(dryWeight) });
            toast.success('Peso seco registrado');
            setDryOpen(false);
            load();
        } catch (e) { toast.error('Erro ao registrar peso seco'); }
    };

    const handleDelete = async () => {
        if (!window.confirm('Remover esta planta?')) return;
        try {
            await api.delete(`/plants/${id}`);
            toast.success('Planta removida');
            navigate('/plants');
        } catch (e) { toast.error('Erro ao remover'); }
    };

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
    if (!plant) return <Box sx={{ p: 3 }}><Typography>Planta não encontrada.</Typography></Box>;

    const archived = plant.status === 'archived';
    const harvested = archived && plant.endType === 'harvested';
    const canDry = harvested && plant.dryWeight == null;

    return (
        <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <IconButton onClick={() => navigate('/plants')}><ArrowBackIcon /></IconButton>
                <Typography variant="h4">🌿 {plant.name}</Typography>
            </Box>

            <Paper sx={{ p: 3 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="textSecondary">Strain</Typography>
                        <Typography variant="body1">{plant.strain || '—'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="textSecondary">Ciclo (estágio)</Typography>
                        <Box sx={{ mt: 0.5 }}>
                            <Chip label={STAGE_LABELS[plant.growthStage] || plant.growthStage} color="primary" size="small" />
                            {archived && (
                                <Chip label={harvested ? 'Colhida' : 'Finalizada'} color={harvested ? 'success' : 'default'} size="small" sx={{ ml: 1 }} />
                            )}
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="textSecondary">Início</Typography>
                        <Typography variant="body1">
                            {plant.startDate ? new Date(plant.startDate).toLocaleDateString('pt-BR') : '—'}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="textSecondary">Tempo de vida</Typography>
                        <Typography variant="body1">
                            {plant.startDate
                                ? `${Math.max(0, Math.round((Date.now() - new Date(plant.startDate)) / 86400000))} dia(s)`
                                : '—'}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="textSecondary">Quantidade de rega (ml)</Typography>
                        <Typography variant="body1">{plant.wateringAmount || '—'}</Typography>
                    </Grid>
                    {harvested && plant.wetWeight != null && (
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="textSecondary">Peso úmido</Typography>
                            <Typography variant="body1">💧 {plant.wetWeight}g</Typography>
                        </Grid>
                    )}
                    {harvested && plant.dryWeight != null && (
                        <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="textSecondary">Peso seco</Typography>
                            <Typography variant="body1">🌿 {plant.dryWeight}g</Typography>
                        </Grid>
                    )}
                    {plant.notes && (
                        <Grid item xs={12}>
                            <Typography variant="caption" color="textSecondary">Notas</Typography>
                            <Typography variant="body2">{plant.notes}</Typography>
                        </Grid>
                    )}
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button variant="contained" startIcon={<EditIcon />} onClick={openEdit}>Editar</Button>
                    {!archived && (
                        <Button variant="contained" color="warning" onClick={openFinish}>Colher / Matar</Button>
                    )}
                    {canDry && (
                        <Button variant="contained" color="success" startIcon={<GrassIcon />} onClick={openDry}>
                            Registrar peso seco
                        </Button>
                    )}
                    <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={handleDelete}>
                        Remover
                    </Button>
                </Box>
            </Paper>

            {/* Diálogo: Editar */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Editar Planta</DialogTitle>
                <DialogContent>
                    <TextField fullWidth margin="dense" label="Nome" value={form.name || ''}
                        onChange={e => setForm({ ...form, name: e.target.value })} />
                    <TextField fullWidth margin="dense" label="Strain" value={form.strain || ''}
                        onChange={e => setForm({ ...form, strain: e.target.value })} />
                    <TextField select fullWidth margin="dense" label="Estágio (ciclo)" value={form.growthStage || 'seedling'}
                        onChange={e => setForm({ ...form, growthStage: e.target.value })}>
                        {STAGES.map(s => <MenuItem key={s} value={s}>{STAGE_LABELS[s]}</MenuItem>)}
                    </TextField>
                    <TextField fullWidth margin="dense" type="date" label="Início (dias de vida já passados)"
                        InputLabelProps={{ shrink: true }} value={form.startDate || ''}
                        onChange={e => setForm({ ...form, startDate: e.target.value })} />
                    <TextField fullWidth margin="dense" type="number" label="Quantidade de rega (ml)" value={form.wateringAmount || 100}
                        onChange={e => setForm({ ...form, wateringAmount: Number(e.target.value) })} />
                    <TextField fullWidth margin="dense" multiline minRows={2} label="Notas" value={form.notes || ''}
                        onChange={e => setForm({ ...form, notes: e.target.value })} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleEdit}>Salvar</Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo: Finalizar */}
            <Dialog open={finishOpen} onClose={() => setFinishOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Finalizar Planta</DialogTitle>
                <DialogContent>
                    <TextField select fullWidth margin="dense" label="Tipo de finalização" value={finishType}
                        onChange={e => setFinishType(e.target.value)}>
                        <MenuItem value="harvested">🌿 Colher</MenuItem>
                        <MenuItem value="killed">💀 Matar</MenuItem>
                    </TextField>
                    {finishType === 'harvested' && (
                        <TextField fullWidth margin="dense" type="number" label="Peso úmido da colheita (g)"
                            value={wetWeight} onChange={e => setWetWeight(e.target.value)} />
                    )}
                    <Typography variant="caption" color="textSecondary">
                        {finishType === 'harvested'
                            ? 'Após ~30 dias de secagem, registre o peso seco pelo botão abaixo.'
                            : 'A planta será arquivada sem registro de colheita.'}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFinishOpen(false)}>Cancelar</Button>
                    <Button variant="contained" color={finishType === 'killed' ? 'error' : 'success'} onClick={handleFinish}>
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo: Peso Seco */}
            <Dialog open={dryOpen} onClose={() => setDryOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Peso Seco (após 30 dias)</DialogTitle>
                <DialogContent>
                    <TextField fullWidth margin="dense" type="number" label="Peso seco (g)"
                        value={dryWeight} onChange={e => setDryWeight(e.target.value)} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDryOpen(false)}>Cancelar</Button>
                    <Button variant="contained" color="success" onClick={handleDry}>Registrar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
